import { useState, useEffect } from 'react';
import { useGameStore, useJwt, useAuthHeaders, EventBus, groupZones } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Menu, MenuItem, MenuLabel, Icon } from '../ds/index.js';

interface ZoneInfo {
  slug: string;
  displayName: string;
  isDisabled?: boolean;
  isArrival?: boolean;
}

/**
 * ZonePicker — choix de la zone, ouvert depuis la pastille de lieu en haut à
 * gauche : menu sous la pastille sur ordinateur, panneau du bas sur mobile.
 *
 * Note backlog : "Pathfinding A* — V2" pour la marche automatique vers les portails.
 */
export function ZonePicker({ onClose }: { onClose: () => void }) {
  const jwt = useJwt();
  const currentZoneId = useGameStore((s) => s.currentZoneId);
  const currentSlug = currentZoneId?.split(':')[1] ?? '';
  const tenantId = currentZoneId?.split(':')[0] ?? '';
  const authHeaders = useAuthHeaders(tenantId);
  const isMobile = useIsMobile();

  const [zones, setZones] = useState<ZoneInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jwt || !tenantId) return;
    fetch(`${API_BASE}/world/zones`, { headers: authHeaders })
      .then((res) => res.json())
      .then((data: Array<{ slug: string; displayName: string; isDisabled?: boolean; isArrival?: boolean }>) => {
        setZones(data.map((z) => ({ slug: z.slug, displayName: z.displayName, isDisabled: z.isDisabled === true, isArrival: z.isArrival === true })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [jwt, tenantId]);

  const handleSelect = (slug: string) => {
    onClose();
    if (slug === currentSlug) return;
    EventBus.emit('zone:transition-requested', { targetSlug: slug, spawnId: 'default' });
  };

  const groups = groupZones(zones);

  return (
    <Menu
      label="Changer de zone"
      onClose={onClose}
      sheet={isMobile}
      // 12 px sous la pastille ; hauteur bornée à la place entre les barres du haut et du bas
      // (mesurées) : une longue liste défile.
      style={isMobile ? undefined : {
        position: 'absolute', top: 'calc(100% + 12px)', left: 0, width: 300,
        maxHeight: 'calc(100dvh - var(--pl-hud-top) - 12px - var(--pl-hud-bottom) - 16px)', overflowY: 'auto', overscrollBehavior: 'contain',
      }}
    >
      <MenuLabel>Changer de zone</MenuLabel>
      {loading && <p style={{ margin: 0, padding: '10px', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Chargement…</p>}
      {!loading && zones.length === 0 && (
        <p style={{ margin: 0, padding: '10px', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Aucune autre zone pour l'instant.</p>
      )}
      {/* Le campus en haut, puis un groupe par bâtiment (ordre de l'API, zoneOrder.ts). */}
      {groups.map((group, gi) => [
        ...(groups.length > 1 ? [<MenuLabel key={`g${gi}`}>{group.title}</MenuLabel>] : []),
        ...group.zones.map((zone) => {
        const here = zone.slug === currentSlug;
        return (
          <MenuItem
            key={zone.slug}
            icon={<Icon name="map-pin" size={16} className={here ? 'pl-menu-check' : 'pl-menu-icon'} />}
            checked={here}
            trailing={here
              ? <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)' }}>Tu es ici</span>
              // Carte désactivée : visible des seuls OWNER/ADMIN, fermée aux autres.
              : zone.isDisabled ? <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)' }}>Désactivée</span> : undefined}
            onSelect={() => handleSelect(zone.slug)}
          >
            {zone.displayName}
          </MenuItem>
        );
        }),
      ])}
    </Menu>
  );
}
